import { Stack } from "@mui/material";
import { Text } from "@src/appV2/redesign/system/ui-components";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format } from "date-fns";
import { ceil, isEmpty, round } from "lodash";
import pluralize from "pluralize";

import { DotSeparatedText } from "../../Shift/WorkerShift/DotSeparatedText";
import { VisitStatusAlertPill } from "../BookedVisitDetails/StatusAlertPill";
import {
  readableVisitTypeMapping,
  shortenedDaysOfWeekMapping,
  VisitPricingTypes,
  VisitTypes,
} from "../constants";
import { type PatientData, type VisitType, type VisitWithCaseData } from "../types";
import { VisitTimeLabel } from "./VisitTimeLabel";

interface VisitCardSummaryInfoProps {
  visit: VisitWithCaseData;
  patient: PatientData | undefined;
  workplaceName: string;
}

function getVisitCardTitle(deadline: Date | string, type: VisitType) {
  const formattedDeadline = format(new Date(deadline), "eee, MMM d");
  if (type !== VisitTypes.REGULAR) {
    return `By ${formattedDeadline}`;
  }

  return `First visit by ${formattedDeadline}`;
}

function getVisitDetailsByVisit(visit: VisitWithCaseData): string[] {
  const { type, pricingType, visitsPerWeek, daysOfWeek } = visit.attributes;
  const details = [readableVisitTypeMapping[type] ?? type];
  if (type !== VisitTypes.REGULAR) {
    return details;
  }

  if (pricingType === VisitPricingTypes.PER_VISIT) {
    details.push(`${visitsPerWeek} ${pluralize("visit", visitsPerWeek)} per week`);
  } else if (pricingType === VisitPricingTypes.PER_HOUR && daysOfWeek && !isEmpty(daysOfWeek)) {
    const daysOfWeekString = daysOfWeek.map((day) => shortenedDaysOfWeekMapping[day]).join("/");
    details.push(`${daysOfWeekString} ${pluralize("visit", daysOfWeek.length)}`);
  }

  return details;
}

function roundPatientDistance(distance: number) {
  if (distance > 1) {
    return round(distance);
  }

  if (distance < 0.1) {
    return ceil(distance, 1);
  }

  return round(distance, 1);
}

export function VisitCardSummaryInfo(props: VisitCardSummaryInfoProps) {
  const { visit, patient, workplaceName } = props;
  const { deadline, type } = visit.attributes;

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <VisitStatusAlertPill visit={visit} />
        {deadline && (
          <Text semibold variant="h6">
            {getVisitCardTitle(deadline, type)}
          </Text>
        )}
        <VisitTimeLabel visit={visit} />
      </Stack>
      <DotSeparatedText>
        {getVisitDetailsByVisit(visit).map((detail) => (
          <Text key={detail} color={(theme) => theme.palette.text.secondary} variant="body1">
            {detail}
          </Text>
        ))}
      </DotSeparatedText>
      {patient && (
        <Stack direction="row" alignItems="baseline" spacing={3}>
          <Text color={(theme) => theme.palette.text.secondary} variant="body1">
            {patient.attributes.externalPatientIdentifier}
          </Text>
          <Text color={(theme) => theme.palette.text.tertiary} variant="body2">
            {roundPatientDistance(patient.attributes.distanceToWorkerInMiles)} mi
          </Text>
        </Stack>
      )}
      <Text color={(theme) => theme.palette.text.secondary} variant="body1">
        {workplaceName}
      </Text>
    </Stack>
  );
}
